/**
 * Pagination Utility Module
 *
 * This module provides functions to handle pagination logic,
 * including calculating pagination metadata and slicing data arrays.
 */

/**
 * Pagination constants
 */
export enum PAGINATION {
    DEFAULT_PAGE      = 1,
    DEFAULT_PAGE_SIZE = 10,
    MAX_PAGE_SIZE     = 25
}

/**
 * Paginated result interface
 */
export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/**
 * Pagination Utility Class
 */
export class PaginationUtil {
  /**
   * Applies pagination to any array of items
   * @param items - Array of items to paginate
   * @param page - Current page number (1-based)
   * @param limit - Number of items per page
   * @returns Paginated result with metadata
   */
  public static paginate<T>(
    items: T[],
    page: number = PAGINATION.DEFAULT_PAGE,
    limit: number = PAGINATION.DEFAULT_PAGE_SIZE
  ): PaginatedResult<T> {
    // Validate that items array is defined and not null
    if (!items || !Array.isArray(items)) {
      return {
        data: [],
        total: 0,
        page: 1,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
    }

    const total = items.length;

    if (total === 0) {
      return {
        data: [],
        total: 0,
        page: 1,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
    }

    // If limit is -1 or 0, return all items without pagination
    if (limit <= 0) {
      return {
        data: items,
        total,
        page: 1,
        totalPages: 1,
        hasNext: false,
        hasPrev: false
      };
    }

    // Ensure page is at least 1
    const currentPage = Math.max(1, page);
    const totalPages = Math.ceil(total / limit);
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedData = items.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      total,
      page: currentPage,
      totalPages,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1
    };
  }

  /**
   * Gets pagination metadata without slicing the data
   * @param totalItems - Total number of items
   * @param page - Current page number (1-based)
   * @param limit - Number of items per page
   * @returns Pagination metadata only
   */
  public static getPaginationMeta(
    totalItems: number,
    page: number = 1,
    limit: number = 10
  ): Omit<PaginatedResult<any>, 'data'> {
    const currentPage = Math.max(1, page);
    const totalPages = Math.ceil(totalItems / limit);

    return {
      total: totalItems,
      page: currentPage,
      totalPages,
      hasNext: currentPage < totalPages,
      hasPrev: currentPage > 1
    };
  }

  /**
   * Calculates start and end indices for pagination
   * @param page - Current page number (1-based)
   * @param limit - Number of items per page
   * @returns Object with startIndex and endIndex
   */
  public static getIndices(page: number = 1, limit: number = 10): {
    startIndex: number;
    endIndex: number;
  } {
    const currentPage = Math.max(1, page);
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;

    return { startIndex, endIndex };
  }
}
